/**
 * Shared loan details template for both React components and PDF generation
 * This eliminates code duplication between LoanDetailsForOfficerScreen.tsx and loan-details-html-2-pdf-template.html
 */

export interface LoanDetailsTemplateOptions {
  handlebarsMode?: boolean; // Set to true when used with Handlebars
}

/**
 * Helper function to format currency values
 */
export function formatCurrency(val: any): string {
  if (val === undefined || val === null || val === '') return '';
  return `$${Number(val).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

/**
 * Helper function to format currency values without decimal places
 */
export function formatCurrencyNoDecimal(val: any): string {
  if (val === undefined || val === null || val === '') return '';
  return `$${Number(val).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

/**
 * Helper function to format percentage values
 */
export function formatPercent(val: any): string {
  if (val === undefined || val === null || val === '') return '';
  return `${Number(val).toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
}

/**
 * Generates the loan details table rows HTML structure
 * Can be used in both React components and Handlebars templates
 */
export function generateLoanDetailsTableContent(options: LoanDetailsTemplateOptions = {}): string {
  const { handlebarsMode = false } = options;
  
  // Helper to create conditional sections for Handlebars vs React
  const ifBlock = (condition: string, content: string): string => {
    if (handlebarsMode) {
      return `{{#if ${condition}}}\n${content}\n{{/if}}`;
    }
    return `{${condition} && (\n${content}\n)}`;
  };
  
  // Helper to format values based on environment
  const formatValue = (value: string, type: 'currency' | 'percent' | 'text' = 'text'): string => {
    if (handlebarsMode) {
      if (type === 'currency') {
        return `\${{formatNumber ${value}}}`;
      } else if (type === 'percent') {
        return `{{${value}}}%`;
      }
      return `{{${value}}}`;
    }
    
    if (type === 'currency') {
      return `{formatCurrency(${value})}`;
    } else if (type === 'percent') {
      return `{${value}}%`;
    }
    return `{${value}}`;
  };
  
  // Helper to access nested properties based on environment
  const prop = (path: string): string => {
    if (handlebarsMode) {
      return path;
    }
    return path.replace(/\./g, '?.');
  };
  
  // Generate the table content
  return `
  <tr><td colSpan="2" style="height: 5px;"></td></tr>
  
  <!-- Loan Amounts Section -->
  <tr>
    <td${!handlebarsMode ? ' className="font-medium py-1"' : ''}>Total Loan Amount</td>
    <td${!handlebarsMode ? ' className="value-cell text-right py-1"' : ' class="value-cell"'}>${formatValue(`(default ${prop('loanDetails.totalLoanAmount.total')} 0)`, 'currency')}</td>
  </tr>
  
  ${ifBlock(prop('loanDetails.totalLoanAmount.acquisitionLoan'), `
  <tr>
    <td${!handlebarsMode ? ' className="indent-1 pl-5 text-gray-600 py-1"' : ' class="indent-1"'}>${handlebarsMode ? '{{loanPurpose.display}}' : '{loanPurpose?.display || "Loan"}'} Amount</td>
    <td${!handlebarsMode ? ' className="value-cell text-right text-gray-600 py-1"' : ' class="value-cell"'}>${formatValue(`(default ${prop('loanDetails.totalLoanAmount.acquisitionLoan')} 0)`, 'currency')}</td>
  </tr>
  `)}
  
  ${ifBlock(prop('loanDetails.totalLoanAmount.rehabHoldback'), `
  <tr>
    <td${!handlebarsMode ? ' className="indent-1 pl-5 text-gray-600 py-1"' : ' class="indent-1"'}>Rehab Holdback</td>
    <td${!handlebarsMode ? ' className="value-cell text-right text-gray-600 py-1"' : ' class="value-cell"'}>${formatValue(prop('loanDetails.totalLoanAmount.rehabHoldback'), 'currency')}</td>
  </tr>
  `)}
  
  <tr><td colSpan="2" style="height: 5px;"></td></tr>
  
  <!-- Monthly Payments Section -->
  ${ifBlock(prop('loanDetails.monthlyPayment.initial'), `
  <tr>
    <td${!handlebarsMode ? ' className="font-medium py-1"' : ''}>Monthly Payment (Initial)</td>
    <td${!handlebarsMode ? ' className="value-cell text-right py-1"' : ' class="value-cell"'}>${formatValue(`(default ${prop('loanDetails.monthlyPayment.initial')} 0)`, 'currency')}</td>
  </tr>
  `)}
  
  ${ifBlock(prop('loanDetails.monthlyPayment.fullyDrawn'), `
  <tr>
    <td${!handlebarsMode ? ' className="indent-1 pl-5 text-gray-600 py-1"' : ' class="indent-1"'}>Monthly Payment (Fully Drawn)</td>
    <td${!handlebarsMode ? ' className="value-cell text-right text-gray-600 py-1"' : ' class="value-cell"'}>${formatValue(prop('loanDetails.monthlyPayment.fullyDrawn'), 'currency')}</td>
  </tr>
  `)}
  
  <tr><td colSpan="2" style="height: 5px;"></td></tr>
  
  <!-- Interest Rate -->
  ${ifBlock(prop('loanDetails.interestRate'), `
  <tr>
    <td${!handlebarsMode ? ' className="font-medium py-1"' : ''}>Interest Rate</td>
    <td${!handlebarsMode ? ' className="value-cell text-right py-1"' : ' class="value-cell"'}>${formatValue(prop('loanDetails.interestRate'), 'percent')}</td>
  </tr>
  `)}
  
  <tr><td colSpan="2" style="height: 5px;"></td></tr>
  
  <!-- Cash Required at Closing Section -->
  <tr>
    <td${!handlebarsMode ? ' className="font-medium py-1"' : ''}>Est. Cash Required at Closing</td>
    <td${!handlebarsMode ? ' className="value-cell text-right py-1"' : ' class="value-cell"'}>${formatValue(prop('loanDetails.estNetCashOutAtClosing'), 'currency')}</td>
  </tr>
  
  <!-- Property Value Section -->
  ${ifBlock(prop('loanInfo.purchasePrice'), `
  <tr>
    <td${!handlebarsMode ? ' className="indent-1 pl-5 text-gray-600 py-1"' : ' class="indent-1"'}>Purchase Price</td>
    <td${!handlebarsMode ? ' className="value-cell text-right text-gray-600 py-1"' : ' class="value-cell"'}>${formatValue(prop('loanInfo.purchasePrice'), 'currency')}</td>
  </tr>
  `)}
  
  ${ifBlock(prop('loanInfo.currentLoanBalance'), `
  <tr>
    <td${!handlebarsMode ? ' className="indent-1 pl-5 text-gray-600 py-1"' : ' class="indent-1"'}>Current Loan Balance</td>
    <td${!handlebarsMode ? ' className="value-cell text-right text-gray-600 py-1"' : ' class="value-cell"'}>${formatValue(prop('loanInfo.currentLoanBalance'), 'currency')}</td>
  </tr>
  `)}
  
  <!-- Fees Section -->
  ${ifBlock(prop('loanDetails.cashRequired.breakdown.originationFee.amount'), `
  <tr>
    <td${!handlebarsMode ? ' className="indent-1 pl-5 text-gray-600 py-1"' : ' class="indent-1"'}>Origination Fee</td>
    <td${!handlebarsMode ? ' className="value-cell text-right text-gray-600 py-1"' : ' class="value-cell"'}>${formatValue(prop('loanDetails.cashRequired.breakdown.originationFee.amount'), 'currency')}</td>
  </tr>
  `)}
  
  <tr>
    <td${!handlebarsMode ? ' className="indent-1 pl-5 text-gray-600 py-1"' : ' class="indent-1"'}>Broker Fee</td>
    <td${!handlebarsMode ? ' className="value-cell text-right text-gray-600 py-1"' : ' class="value-cell"'}>$0</td>
  </tr>
  
  <tr>
    <td${!handlebarsMode ? ' className="indent-1 pl-5 text-gray-600 py-1"' : ' class="indent-1"'}>Prepaid Interest Reserve</td>
    <td${!handlebarsMode ? ' className="value-cell text-right text-gray-600 py-1"' : ' class="value-cell"'}>${formatValue(prop('loanDetails.cashRequired.breakdown.prepaidInterestReserve.amount'), 'currency')}</td>
  </tr>
  
  ${ifBlock(prop('loanDetails.cashRequired.breakdown.perDiemInterest.amount'), `
  <tr>
    <td${!handlebarsMode ? ' className="indent-1 pl-5 text-gray-600 py-1"' : ' class="indent-1"'}>Per-Diem Interest</td>
    <td${!handlebarsMode ? ' className="value-cell text-right text-gray-600 py-1"' : ' class="value-cell"'}>${formatValue(prop('loanDetails.cashRequired.breakdown.perDiemInterest.amount'), 'currency')}</td>
  </tr>
  `)}
  
  ${ifBlock(prop('loanDetails.cashRequired.breakdown.processingFees'), `
  <tr>
    <td${!handlebarsMode ? ' className="indent-1 pl-5 text-gray-600 py-1"' : ' class="indent-1"'}>Loan Processing & Servicing Fees</td>
    <td${!handlebarsMode ? ' className="value-cell text-right text-gray-600 py-1"' : ' class="value-cell"'}>${formatValue(prop('loanDetails.cashRequired.breakdown.processingFees'), 'currency')}</td>
  </tr>
  `)}
  
  ${ifBlock(prop('loanDetails.cashRequired.breakdown.legalFees'), `
  <tr>
    <td${!handlebarsMode ? ' className="indent-1 pl-5 text-gray-600 py-1"' : ' class="indent-1"'}>Est. Legal Costs</td>
    <td${!handlebarsMode ? ' className="value-cell text-right text-gray-600 py-1"' : ' class="value-cell"'}>${formatValue(prop('loanDetails.cashRequired.breakdown.legalFees'), 'currency')}</td>
  </tr>
  `)}
  
  <tr>
    <td${!handlebarsMode ? ' className="indent-1 pl-5 text-gray-600 py-1"' : ' class="indent-1"'}>Third Party Costs<${!handlebarsMode ? 'sup className="text-xs align-super"' : 'span class="superscript"'}>*</${!handlebarsMode ? 'sup' : 'span'}></td>
    <td${!handlebarsMode ? ' className="value-cell text-right text-gray-600 py-1"' : ' class="value-cell"'}>Contact closing agent</td>
  </tr>
  
  <tr><td colSpan="2" style="height: 5px;"></td></tr>
  
  <!-- Equity Contribution -->
  ${ifBlock(prop('loanDetails.isEquityRequired'), `
  <tr>
    <td${!handlebarsMode ? ' className="indent-1 pl-5 text-gray-600 py-1"' : ' class="indent-1"'}>Remaining Equity Contribution<${!handlebarsMode ? 'sup className="text-xs align-super"' : 'span class="superscript"'}>#</${!handlebarsMode ? 'sup' : 'span'}></td>
    <td${!handlebarsMode ? ' className="value-cell text-right text-gray-600 py-1"' : ' class="value-cell"'}>${formatValue(prop('loanDetails.requiredEquityAmount'), 'currency')}</td>
  </tr>
  `)}
  
  <tr><td colSpan="2" style="height: 5px;"></td></tr>
  
  <!-- Additional Details Section -->
  <tr>
    <td${!handlebarsMode ? ' className="font-medium py-1"' : ''}>Additional Details</td>
    <td${!handlebarsMode ? ' className="value-cell"' : ' class="value-cell"'}></td>
  </tr>
  
  ${ifBlock(prop('loanDetails.additionalDetails.loanTerm'), `
  <tr>
    <td${!handlebarsMode ? ' className="indent-1 pl-5 text-gray-600 py-1"' : ' class="indent-1"'}>Loan Term</td>
    <td${!handlebarsMode ? ' className="value-cell text-right text-gray-600 py-1"' : ' class="value-cell"'}>${formatValue(prop('loanDetails.additionalDetails.loanTerm'), 'text')}</td>
  </tr>
  `)}
  
  ${ifBlock(prop('loanDetails.additionalDetails.amortization'), `
  <tr>
    <td${!handlebarsMode ? ' className="indent-1 pl-5 text-gray-600 py-1"' : ' class="indent-1"'}>Amortization</td>
    <td${!handlebarsMode ? ' className="value-cell text-right text-gray-600 py-1"' : ' class="value-cell"'}>${formatValue(prop('loanDetails.additionalDetails.amortization'), 'text')}</td>
  </tr>
  `)}
  
  <tr>
    <td${!handlebarsMode ? ' className="indent-1 pl-5 text-gray-600 py-1"' : ' class="indent-1"'}>Preferred Signing Date</td>
    <td${!handlebarsMode ? ' className="value-cell text-right text-gray-600 py-1"' : ' class="value-cell"'}>As soon as possible</td>
  </tr>
  
  ${ifBlock(handlebarsMode ? 'loanPurpose.display' : 'loanPurpose?.display', `
  <tr>
    <td${!handlebarsMode ? ' className="indent-1 pl-5 text-gray-600 py-1"' : ' class="indent-1"'}>Loan Purpose</td>
    <td${!handlebarsMode ? ' className="value-cell text-right text-gray-600 py-1"' : ' class="value-cell"'}>${handlebarsMode ? '{{loanPurpose.display}}' : '{loanPurpose?.display}'}</td>
  </tr>
  `)}
  
  ${ifBlock(handlebarsMode ? 'exitStrategy.display' : 'exitStrategy?.display', `
  <tr>
    <td${!handlebarsMode ? ' className="indent-1 pl-5 text-gray-600 py-1"' : ' class="indent-1"'}>Exit Strategy</td>
    <td${!handlebarsMode ? ' className="value-cell text-right text-gray-600 py-1"' : ' class="value-cell"'}>${handlebarsMode ? '{{exitStrategy.display}}' : '{exitStrategy?.display}'}</td>
  </tr>
  `)}
  
  ${ifBlock(prop('loanDetails.additionalDetails.lien'), `
  <tr>
    <td${!handlebarsMode ? ' className="indent-1 pl-5 text-gray-600 py-1"' : ' class="indent-1"'}>Lien</td>
    <td${!handlebarsMode ? ' className="value-cell text-right text-gray-600 py-1"' : ' class="value-cell"'}>${formatValue(prop('loanDetails.additionalDetails.lien'), 'text')}</td>
  </tr>
  `)}
  
  ${ifBlock(handlebarsMode ? 'propertyType.display' : 'propertyType?.display', `
  <tr>
    <td${!handlebarsMode ? ' className="indent-1 pl-5 text-gray-600 py-1"' : ' class="indent-1"'}>Property Type</td>
    <td${!handlebarsMode ? ' className="value-cell text-right text-gray-600 py-1"' : ' class="value-cell"'}>${handlebarsMode ? '{{propertyType.display}}' : '{propertyType?.display}'}</td>
  </tr>
  `)}
  
  <tr>
    <td${!handlebarsMode ? ' className="indent-1 pl-5 text-gray-600 py-1"' : ' class="indent-1"'}>Occupancy</td>
    <td${!handlebarsMode ? ' className="value-cell text-right text-gray-600 py-1"' : ' class="value-cell"'}>Non-Owner Occupied</td>
  </tr>
  
  <tr>
    <td${!handlebarsMode ? ' className="indent-1 pl-5 text-gray-600 py-1"' : ' class="indent-1"'}>As-Is Value</td>
    <td${!handlebarsMode ? ' className="value-cell text-right text-gray-600 py-1"' : ' class="value-cell"'}>
      ${handlebarsMode 
        ? `{{#if loanDetails.isPurchasePurpose}}
            \${{formatNumber loanInfo.purchasePrice}}
          {{else}}
            \${{formatNumber loanInfo.asIsValue}}
          {{/if}}`
        : `{formatCurrency(loanInfo?.asIsValue || loanInfo?.asIsPropertyValue || loanInfo?.propertyValue || loanInfo?.currentValue || 
          (loanInfo?.purchasePrice ? loanInfo.purchasePrice : 'Not Available'))}`
      }
    </td>
  </tr>
  
  <tr>
    <td${!handlebarsMode ? ' className="indent-1 pl-5 text-gray-600 py-1"' : ' class="indent-1"'}>Estimated ARV</td>
    <td${!handlebarsMode ? ' className="value-cell text-right text-gray-600 py-1"' : ' class="value-cell"'}>
      ${handlebarsMode 
        ? `\${{formatNumber loanInfo.afterRepairValue}}`
        : `{formatCurrency(loanInfo?.afterRepairValue || loanInfo?.arv || loanInfo?.estimatedARV || 
          (loanInfo?.asIsValue ? Math.round(loanInfo.asIsValue * 1.2) : 'Not Available'))}`
      }
    </td>
  </tr>
  
  ${ifBlock(prop('loanDetails.additionalDetails.loanToCost'), `
  <tr>
    <td${!handlebarsMode ? ' className="indent-1 pl-5 text-gray-600 py-1"' : ' class="indent-1"'}>Loan-to-Cost</td>
    <td${!handlebarsMode ? ' className="value-cell text-right text-gray-600 py-1"' : ' class="value-cell"'}>${formatValue(prop('loanDetails.additionalDetails.loanToCost'), 'percent')}</td>
  </tr>
  `)}
  
  ${ifBlock(prop('loanDetails.totalLoanAmount.rehabHoldback'), `
  <tr>
    <td${!handlebarsMode ? ' className="indent-1 pl-5 text-gray-600 py-1"' : ' class="indent-1"'}>Holdback Amount</td>
    <td${!handlebarsMode ? ' className="value-cell text-right text-gray-600 py-1"' : ' class="value-cell"'}>${formatValue(prop('loanDetails.totalLoanAmount.rehabHoldback'), 'currency')}</td>
  </tr>
  `)}
  
  ${ifBlock(prop('loanDetails.additionalDetails.prepaymentPenalty'), `
  <tr>
    <td${!handlebarsMode ? ' className="indent-1 pl-5 text-gray-600 py-1"' : ' class="indent-1"'}>Prepayment Penalty</td>
    <td${!handlebarsMode ? ' className="value-cell text-right text-gray-600 py-1"' : ' class="value-cell"'}>${formatValue(prop('loanDetails.additionalDetails.prepaymentPenalty'), 'text')}</td>
  </tr>
  `)}
  
  <tr>
    <td${!handlebarsMode ? ' className="indent-1 pl-5 text-gray-600 py-1"' : ' class="indent-1"'}>Exit Fee</td>
    <td${!handlebarsMode ? ' className="value-cell text-right text-gray-600 py-1"' : ' class="value-cell"'}>$0</td>
  </tr>
  
  <tr><td colSpan="2" style="height: 5px;"></td></tr>
  
  <!-- Borrower Qualifications Section -->
  <tr>
    <td${!handlebarsMode ? ' className="font-medium py-1"' : ''}>Borrower Qualifications</td>
    <td${!handlebarsMode ? ' className="value-cell text-right py-1"' : ' class="value-cell"'}>${formatValue(prop('loanDetails.qualificationStatus'), 'text')}</td>
  </tr>
  
  ${ifBlock(handlebarsMode ? 'ficoScore.display' : 'ficoScore?.display', `
  <tr>
    <td${!handlebarsMode ? ' className="indent-1 pl-5 text-gray-600 py-1"' : ' class="indent-1"'}>FICO Score</td>
    <td${!handlebarsMode ? ' className="value-cell text-right text-gray-600 py-1"' : ' class="value-cell"'}>${handlebarsMode ? '{{ficoScore.display}}' : '{ficoScore?.display}'}</td>
  </tr>
  `)}
  
  ${ifBlock(handlebarsMode ? 'experienceLevel.display' : 'experienceLevel?.display', `
  <tr>
    <td${!handlebarsMode ? ' className="indent-1 pl-5 text-gray-600 py-1"' : ' class="indent-1"'}>Real Estate Experience</td>
    <td${!handlebarsMode ? ' className="value-cell text-right text-gray-600 py-1"' : ' class="value-cell"'}>${handlebarsMode ? '{{experienceLevel.display}}' : '{experienceLevel?.display}'}</td>
  </tr>
  `)}
  
  <tr>
    <td${!handlebarsMode ? ' className="indent-1 pl-5 text-gray-600 py-1"' : ' class="indent-1"'}>Minimum Available Liquidity</td>
    <td${!handlebarsMode ? ' className="value-cell text-right text-gray-600 py-1"' : ' class="value-cell"'}>${formatValue(prop('loanDetails.minimumAvailableLiquidity'), 'currency')}</td>
  </tr>
  `;
}

/**
 * Generates the footer notes for the loan details
 */
export function generateLoanDetailsFooterNotes(options: LoanDetailsTemplateOptions = {}): string {
  const { handlebarsMode = false } = options;
  
  if (handlebarsMode) {
    return `
    <p class="footer-notes">* Third party settlement costs - contact closing agent for details
    {{#if loanDetails.requiredEquityAmount}}<br>
     # Prior to disbursement of any construction funds, Stormfield will verify that borrower has met their Remaining Borrower Equity Contribution{{/if}}</p>
    `;
  }
  
  return `
  <p className="mt-4 text-xs text-gray-500">
    <p><sup>*</sup> Third party settlement costs - contact closing agent for details</p>
    {loanDetails?.requiredEquityAmount && (
      <p><sup>#</sup> Prior to disbursement of any construction funds, Stormfield will verify that borrower has met their Remaining Borrower Equity Contribution</p>
    )}
  </p>
  `;
}
